import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, RotateCcw, UserPlus } from 'lucide-react';
import { useTicket, useUpdateTicket } from '@/api/tickets';
import { Avatar, Button, Card, ErrorState, Skeleton, useToast } from '@/components/ui';
import { useAuth } from '@/app/auth/useAuth';
import { formatDateTime, formatRelative } from '@/lib/format';
import { CHANNEL_LABEL } from '../tickets/meta';
import { Conversation } from '../tickets/Conversation';
import { ChannelIcon, PriorityBadge, SlaBadge, StatusBadge } from '../tickets/TicketBadges';
import { TicketSidebar } from '../tickets/TicketSidebar';

function TicketSkeleton() {
  return (
    <div role="status" aria-label="Loading ticket" className="space-y-4">
      <Skeleton className="h-20 w-full rounded-2xl" />
      <div className="flex gap-4">
        <Skeleton className="h-[60vh] flex-1 rounded-2xl" />
        <Skeleton className="hidden h-[60vh] w-80 rounded-2xl lg:block" />
      </div>
    </div>
  );
}

export function TicketPage() {
  const { ticketId } = useParams();
  const { user, can } = useAuth();
  const toast = useToast();
  const ticket = useTicket(ticketId);
  const update = useUpdateTicket();

  if (ticket.isPending) return <TicketSkeleton />;
  if (ticket.error) {
    return (
      <Card>
        <ErrorState error={ticket.error} onRetry={() => void ticket.refetch()} />
      </Card>
    );
  }
  const t = ticket.data;
  const resolved = t.status === 'solved' || t.status === 'closed';
  const act = (input: Parameters<typeof update.mutate>[0]['input'], msg: string) =>
    update.mutate(
      { id: t.id, input },
      { onSuccess: () => toast.success(msg), onError: (e) => toast.error(e.message) },
    );

  return (
    <div className="flex flex-col gap-4 lg:h-[calc(100dvh-8rem)]">
      <Card className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:p-5">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <Link
            to="/inbox"
            className="mt-1 rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-900"
            aria-label="Back to inbox"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <Avatar
            name={t.customer?.name ?? 'Customer'}
            src={t.customer?.avatarUrl}
            size="md"
            className="mt-0.5 hidden sm:inline-flex"
          />
          <div className="min-w-0">
            <h1 className="text-lg leading-snug font-bold sm:text-xl">{t.subject}</h1>
            <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-slate-500">
              <span className="font-medium text-slate-700">{t.customer?.name}</span>
              <span className="font-mono">#{t.number}</span>
              <span className="flex items-center gap-1">
                <ChannelIcon channel={t.channel} /> {CHANNEL_LABEL[t.channel]}
              </span>
              <span title={formatDateTime(t.createdAt)}>Opened {formatRelative(t.createdAt)}</span>
              <span className="flex flex-wrap gap-1.5">
                <StatusBadge status={t.status} />
                <PriorityBadge priority={t.priority} />
                <SlaBadge ticket={t} />
              </span>
            </div>
          </div>
        </div>
        {can('update', 'Ticket') && (
          <div className="flex shrink-0 gap-2 sm:self-center">
            {!t.assigneeId && (
              <Button
                variant="secondary"
                size="sm"
                loading={update.isPending}
                onClick={() => act({ assigneeId: user.id }, 'Ticket assigned to you')}
              >
                <UserPlus className="h-4 w-4" /> Take it
              </Button>
            )}
            {resolved ? (
              <Button
                variant="secondary"
                size="sm"
                loading={update.isPending}
                onClick={() => act({ status: 'open' }, 'Ticket reopened')}
              >
                <RotateCcw className="h-4 w-4" /> Reopen
              </Button>
            ) : (
              <Button
                size="sm"
                loading={update.isPending}
                onClick={() => act({ status: 'solved' }, `#${t.number} marked solved`)}
              >
                <CheckCircle2 className="h-4 w-4" /> Mark solved
              </Button>
            )}
          </div>
        )}
      </Card>

      <div className="flex min-h-0 flex-1 flex-col gap-4 lg:flex-row">
        <Card className="dot-canvas flex min-h-[30rem] min-w-0 flex-1 flex-col overflow-hidden">
          <Conversation ticket={t} />
        </Card>
        <Card className="p-5 lg:w-[22rem] lg:shrink-0 lg:overflow-y-auto">
          <TicketSidebar ticket={t} />
        </Card>
      </div>
    </div>
  );
}
